import { _decorator, CCInteger, input, Input, EventKeyboard, Enum, warn, log, error, Tween, Node, tween } from 'cc';
import { DEV, EDITOR, PREVIEW } from 'cc/env';
import Delay from '../../Utils/Delay';
import Processor from '../Misc/Processor';
import KeyCodeMap from '../../Utils/KeyCodeMap';
import EnhanceAnimation from './EnhanceAnimation';
import OrientationManager from '../../../OrientationManager';
import NodeAction, { NodeActionType } from '../../CCClass/Animation/NodeAction';
import BaseAction, { OrientationLimitType } from '../../CCClass/Animation/BaseAction';
import AudioAction, { AudioActionType, AudioType } from '../../CCClass/Animation/AudioAction';
import ParticleAction, { ParticleActionType } from '../../CCClass/Animation/ParticleAction';
import ProcessorAction, { ProcessorActionType } from '../../CCClass/Animation/ProcessorAction';
import AnimationAction, { AnimationActionType } from '../../CCClass/Animation/AnimationAction';
import MovingEffectAction, { MovingEffectActionType, MovingEffectType } from '../../CCClass/Animation/MovingEffectAction';
import SpineAnimationAction, { SpineAnimationActionType } from '../../CCClass/Animation/SpineAnimationAction';
import DragonBoneAnimationAction, { DragonBoneAnimationActionType } from '../../CCClass/Animation/DragonBoneAnimationAction';
import EnhanceAnimationAction, { EnhanceAnimationActionType } from '../../CCClass/Animation/EnhanceAnimationAction';
import BezierMovingEffect, { BezierMovingEffectOption } from '../MovingEffect/BezierMovingEffect';
import TweenMovingEffect from '../MovingEffect/TweenMovingEffect';
import TweenUtils from '../../Utils/TweenUtils';
import { convertCenterPointToWorldSpace } from '../../Utils/Utility';
import MultiThemeComponent from '../MultiTheme/MultiThemeComponent';
import Particle2DAction from '../../CCClass/Animation/Particle2DAction';
import NeutrinoParticleAction, { NeutrinoParticleActionType } from '../../CCClass/Animation/NeutrinoParticleAction';
import NeutrinoComponentBase from '../NeutrinoParticle/NeutrinoComponentBase';
import VapVideoAction from '../../CCClass/Animation/VapVideoAction';

const { ccclass, property, executeInEditMode } = _decorator;

enum EventType {
    ANIMATION_INTERRUPT = 'EnhanceAnimationClip_animationInterrupt', // 打斷 Cocos Animation 的等待播放
    CLIP_END = 'EnhanceAnimationClip_end', // 動畫完全結束, 需透過 KeyFrame 參數 `isEnd` 設定時機
}

@ccclass('EnhanceAnimationKeyFrame')
export class EnhanceAnimationKeyFrame {
    @property({
        type: CCInteger,
        displayName: DEV && '開始時間',
        tooltip: DEV && '延遲多久後開始表演(startDelay), 單位: 毫秒',
        visible(this: EnhanceAnimationKeyFrame) {
            return !this.enableRandomDelay;
        },
    })
    public readonly startDelay: number = 0;
    @property({ displayName: DEV && '啟用隨機時間', tooltip: DEV && '隨機延遲表演時間的開關' })
    public readonly enableRandomDelay: boolean = false;
    @property({
        type: CCInteger,
        displayName: DEV && '最快開始時間',
        tooltip: DEV && '最快延遲多久後開始表演(startDelay), 單位: 毫秒',
        visible(this: EnhanceAnimationKeyFrame) {
            return this.enableRandomDelay;
        },
    })
    public readonly startMinDelay: number = 0;
    @property({
        type: CCInteger,
        displayName: DEV && '最慢開始時間',
        tooltip: DEV && '最慢延遲多久後開始表演(startDelay), 單位: 毫秒',
        visible(this: EnhanceAnimationKeyFrame) {
            return this.enableRandomDelay;
        },
    })
    public readonly startMaxDelay: number = 0;
    @property({ displayName: DEV && '發射事件', tooltip: DEV && 'emit至GameEvent的事件' })
    public readonly emitEvent: string = '';
    @property({ tooltip: DEV && '是否為發送全域事件' })
    public readonly isGlobalEvent: boolean = false;
    @property({ displayName: DEV && '限制MultiTheme', tooltip: DEV && '限制在MultiTheme為多少時才會播放此Clip, -1為無限制', type: CCInteger })
    public readonly limitMultiTheme: number = -1;
    @property({
        tooltip: DEV && '目標MultiTheme',
        type: MultiThemeComponent,
        visible(this: EnhanceAnimationKeyFrame) {
            return this.limitMultiTheme >= 0;
        },
    })
    public readonly targetMultiThemeComponent: MultiThemeComponent<any> = null;
    @property({ type: [NodeAction], tooltip: DEV && '節點行為', group: '節點' })
    public readonly nodes: NodeAction[] = [];
    @property({ type: [ParticleAction], tooltip: DEV && '表演的粒子', displayName: DEV && '3D粒子', group: '粒子' })
    public readonly particles: ParticleAction[] = [];
    @property({ type: [Particle2DAction], tooltip: DEV && '表演的2D粒子', displayName: DEV && '2D粒子', group: '粒子' })
    public readonly particle2ds: Particle2DAction[] = [];
    @property({ type: [NeutrinoParticleAction], tooltip: DEV && '表演的Neutrino粒子', displayName: DEV && 'Neutrino粒子', group: '粒子' })
    public readonly neutrinoParticles: NeutrinoParticleAction[] = [];
    @property({ type: [SpineAnimationAction], tooltip: DEV && '表演的Spine', group: DEV && 'Spine動畫' })
    public readonly skeletons: SpineAnimationAction[] = [];
    @property({ type: [VapVideoAction], tooltip: DEV && '表演的 Vap', group: DEV && 'Vap動畫' })
    public readonly vapVideos: VapVideoAction[] = [];
    @property({ type: [DragonBoneAnimationAction], tooltip: DEV && '表演的DragonBone', group: DEV && 'DragonBone動畫' })
    public readonly armatureDisplays: DragonBoneAnimationAction[] = [];
    @property({ type: [AnimationAction], tooltip: DEV && '表演的動畫', group: DEV && 'Cocos動畫' })
    public readonly animations: AnimationAction[] = [];
    @property({ type: [EnhanceAnimationAction], tooltip: DEV && '表演的增強型動畫', group: DEV && '增強型動畫' })
    public readonly enhanceAnimations: EnhanceAnimationAction[] = [];
    @property({ type: [MovingEffectAction], tooltip: DEV && '飛行表演', group: DEV && '飛行表演' })
    public readonly movingEffects: MovingEffectAction[] = [];
    @property({ type: [ProcessorAction], tooltip: DEV && '表演的Processor', group: DEV && 'Processor' })
    public readonly processors: ProcessorAction[] = [];
    @property({ type: [AudioAction], tooltip: DEV && '播放的音效', group: DEV && '音效' })
    public readonly audios: AudioAction[] = [];

    // た章낙...지魚こ 用來讓 build 完成後識別為要刪除的部分
    @property
    private _annotation: string = '';
    @property({ displayName: DEV && '註解', displayOrder: 0 })
    protected get annotation() {
        return this._annotation.substring(3, this._annotation.length - 3);
    }
    protected set annotation(value: string) {
        this._annotation = `た章낙${value}지魚こ`;
    }
}

@ccclass('EnhanceAnimationClip')
@executeInEditMode
export default class EnhanceAnimationClip extends Processor {
    public static readonly EVENT_TYPE = { ...EventType, ...Processor.EVENT_TYPE };
    private static _instanceTweens: Map<Node, Tween<any>[]> = new Map();

    @property({ displayName: DEV && '動畫名稱', tooltip: DEV && '動畫名稱' })
    public readonly clipName: string = '';

    @property({ type: CCInteger, displayName: DEV && '動畫時長', tooltip: DEV && '動畫時長 (單位: 毫秒)' })
    public readonly duration: number = 0;

    @property({
        type: Enum(KeyCodeMap),
        displayName: DEV && 'Debug鍵盤',
        tooltip: DEV && '[DEBUG功能] 按下鍵盤哪一顆按鈕可以播放表演\r\n 若是要綁定數字鍵盤，需加上`NUM_`，Ex: NUM_1',
    })
    public readonly debugKey: KeyCodeMap = KeyCodeMap.None;

    @property({ visible: true, tooltip: DEV && 'auto replay key frame when view is rotated' })
    private _autoReplayWhenRotated = false;

    @property({ displayName: DEV && '真實時間延遲', tooltip: DEV && '打勾的話, KeyFrame延遲採用 Delay.timeout 處理' })
    private readonly isRealTime: boolean = false;

    // た章낙...지魚こ 用來讓 build 完成後識別為要刪除的部分
    @property
    private _annotation: string = '';

    @property({ type: [EnhanceAnimationKeyFrame], tooltip: DEV && '每個關鍵幀的動畫設定' })
    private readonly keyFrames: EnhanceAnimationKeyFrame[] = [];

    private _needAutoReplayWhenRotated = false;
    private _currentTime: number = 0;
    private _stopExecution: () => void = null;

    public get currentTime() {
        return this._currentTime;
    }
    private set currentTime(value: number) {
        if (value < 0) {
            warn(`[EnhanceAnimationClip] currentTime should not be negative, but got ${value}`);
            value = 0;
        }
        if (value === this._currentTime) {
            return;
        }
        this._currentTime = value;
    }

    @property({ displayName: DEV && '註解', displayOrder: 0 })
    protected get annotation() {
        return this._annotation.substring(3, this._annotation.length - 3);
    }
    protected set annotation(value: string) {
        this._annotation = `た章낙${value}지魚こ`;
    }

    // tweens from All EnhanceAnimationClip
    private get _tweens() {
        return EnhanceAnimationClip._instanceTweens;
    }

    public resetInEditor() {
        // to ensure auto replay when rotated is enabled in new added component by default
        this._autoReplayWhenRotated = true;
    }

    public onLoad(): void {
        if (EDITOR) {
            if (!this.getComponent(EnhanceAnimation)) {
                this.addComponent(EnhanceAnimation);
            }
            return;
        }
        if ((!this.game || this.game.config.debugKey) && this.debugKey !== KeyCodeMap.None) {
            input.on(
                Input.EventType.KEY_DOWN,
                (e: EventKeyboard) => {
                    if (+e.keyCode === this.debugKey) {
                        this.play(0);
                    }
                },
                this,
            );
        }
        this.keyFrames.sort((a, b) => {
            const enableRandomDelayA = a.enableRandomDelay ? a.startMinDelay : a.startDelay;
            const enableRandomDelayB = b.enableRandomDelay ? b.startMinDelay : b.startDelay;
            return enableRandomDelayA - enableRandomDelayB;
        });
        this.processorName = this.clipName;
        this._checkIsAutoReplayWhenRotatedNeeded();
    }

    /**
     * @param startTime play from the specific time (in seconds)
     */
    public play(startTime: number, skipAudioEffect = false) {
        return super.play(startTime, skipAudioEffect);
    }

    protected *run(startTime: number, skipAudioEffect = false) {
        // reset current time when start playing
        this.currentTime = 0;
        const durationDelay = this.duration > 0 ? this._delay(this.duration) : Promise.resolve();
        const executionDelay = new Promise<void>((resolve) => {
            const execute = (startTimeArg: number) => {
                [, this._stopExecution] = Processor.run(this._playKeyFrames(startTimeArg, skipAudioEffect), async (isEndNaturely) => {
                    if (isEndNaturely) {
                        resolve();
                    }
                });
            };
            if (this._needAutoReplayWhenRotated) {
                this.game?.orientation.on(
                    OrientationManager.EVENT_TYPE.ORIENTATION_CHANGE,
                    () => {
                        // restart execution when view is rotated
                        this._stopExecution();
                        execute(this.currentTime);
                    },
                    this,
                );
            }
            execute(startTime);
        });
        yield Promise.all([executionDelay, durationDelay]);
    }

    protected update(dt: number) {
        if (this.isRunning) {
            this.currentTime += dt;
        }
    }

    protected onDestroy() {
        this.stop(false);
    }

    protected async finish() {
        this._stopExecution?.();
        this.game?.orientation?.targetOff(this);
    }

    private _checkIsAutoReplayWhenRotatedNeeded() {
        this._needAutoReplayWhenRotated =
            this._autoReplayWhenRotated &&
            this.keyFrames.some((keyframe) =>
                Object.values(keyframe)
                    .map((value) => {
                        // expect all array values are actions
                        if (value instanceof Array) {
                            return (value as BaseAction[]).some((action) => action.orientationLimit !== OrientationLimitType.NoLimit);
                        }
                        return false;
                    })
                    .some(Boolean),
            );
    }

    private *_playKeyFrames(startTime: number, skipAudioEffect: boolean) {
        for (let i = 0; i < this.keyFrames.length; i++) {
            const keyframe = this.keyFrames[i];
            const startDelay =
                (keyframe.enableRandomDelay ? Math.random() * (keyframe.startMaxDelay - keyframe.startMinDelay) + keyframe.startMinDelay : keyframe.startDelay) / 1000;
            const actualDelay = startDelay - startTime;
            if (actualDelay > 0) {
                yield this._delay(actualDelay * 1000);
                startTime = startDelay;
            }
            if (this.isValid) {
                this._playKeyFrame(keyframe, Math.max(0, startTime - startDelay), skipAudioEffect);
            } else {
                break;
            }
        }
    }

    private _playKeyFrame(keyframe: EnhanceAnimationKeyFrame, currentTime: number, skipAudioEffect = false) {
        if (keyframe.limitMultiTheme > -1 && keyframe.targetMultiThemeComponent) {
            if (keyframe.targetMultiThemeComponent.theme !== keyframe.limitMultiTheme) {
                return;
            }
        }
        if (keyframe.emitEvent !== '') {
            if (keyframe.isGlobalEvent) {
                EnhanceAnimation.EVENT_TARGET.emit(keyframe.emitEvent, this.clipName);
                if (PREVIEW) {
                    log(`[增強動畫] ${this.clipName} 觸發全域事件: ${keyframe.emitEvent}`);
                }
            } else {
                this.node?.emit(keyframe.emitEvent, this.clipName);
                if (PREVIEW) {
                    log(`[增強動畫] ${this.clipName} 觸發事件: ${keyframe.emitEvent}`);
                }
            }
        }
        this._executeNodeActions(keyframe.nodes, currentTime);
        this._executeAnimationActions(keyframe.animations, currentTime);
        this._executeEnhanceAnimationActions(keyframe.enhanceAnimations, currentTime);
        this._executeParticleActions(keyframe.particles);
        this._executeParticle2DActions(keyframe.particle2ds);
        this._executeNeutrinoParticleActions(keyframe.neutrinoParticles);
        this._executeSkeletonActions(keyframe.skeletons, currentTime);
        this._executeVapVideoActions(keyframe.vapVideos);
        this._executeDragonBoneActions(keyframe.armatureDisplays, currentTime);
        this._executeProcessorActions(keyframe.processors);
        this._executeMovingEffectActions(keyframe.movingEffects);
        if (!skipAudioEffect) {
            this._executeAudioActions(keyframe.audios, currentTime);
        }
    }

    private _executeNodeActions(actions: NodeAction[], currentTime: number) {
        actions.forEach((action) => {
            if (!action || !action.nodeTarget) {
                return;
            }
            if (action.orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (action.orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (action.orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }
            if (action.updateZIndex) {
                action.nodeTarget.zIndex = action.zIndex;
            }
            if (action.updateParentNode) {
                action.nodeTarget.parent = action.parentNode;
            }
            if (action.updateMultiTheme) {
                let { theme } = action;
                if (action.updateRandomMultiTheme && action.randomMultiThemeList.length > 0) {
                    const totalWeight = action.randomMultiThemeList.reduce((sum, item) => sum + item.randomWeight, 0);
                    const randomValue = Math.random() * totalWeight;
                    let cumulativeWeight = 0;

                    for (const item of action.randomMultiThemeList) {
                        cumulativeWeight += item.randomWeight;
                        if (randomValue <= cumulativeWeight) {
                            ({ theme } = item);
                            break;
                        }
                    }
                }
                action.nodeTarget.getComponents('MultiThemeComponent').forEach((comp) => {
                    (comp as MultiThemeComponent<any>).theme = theme;
                });
            }
            if (action.type === NodeActionType.Active) {
                action.nodeTarget.active = true;
            } else if (action.type === NodeActionType.Inactive) {
                action.nodeTarget.active = false;
            } else if (action.type === NodeActionType.Show) {
                action.nodeTarget.opacity = 255;
            } else if (action.type === NodeActionType.Hide) {
                action.nodeTarget.opacity = 0;
            } else if (action.type === NodeActionType.FadeIn) {
                const tw = TweenUtils.fadeIn(
                    action.nodeTarget,
                    action.fadeDuration / 1000,
                    false,
                    action.fadeAdjustActive,
                    action.fadeUseCustomCurve && ((t) => action.fadeCustomCurve.evaluate(t)),
                )
                    ?.call(() => {
                        this._popTween(action.nodeTarget, tw);
                    })
                    .start(currentTime);
                this._pushTween(action.nodeTarget, tw);
            } else if (action.type === NodeActionType.FadeOut) {
                const tw = TweenUtils.fadeOut(
                    action.nodeTarget,
                    action.fadeDuration / 1000,
                    false,
                    action.fadeAdjustActive,
                    action.fadeUseCustomCurve && ((t) => action.fadeCustomCurve.evaluate(t)),
                )
                    ?.call(() => {
                        this._popTween(action.nodeTarget, tw);
                    })
                    .start(currentTime);
                this._pushTween(action.nodeTarget, tw);
            } else if (action.type === NodeActionType.TweenStop) {
                const tws = this._tweens.get(action.nodeTarget);
                if (tws) {
                    tws.forEach((tw) => tw.stop());
                    this._tweens.delete(action.nodeTarget);
                }
            } else if (action.type === NodeActionType.TweenJumpToEnd) {
                const tws = this._tweens.get(action.nodeTarget);
                if (tws) {
                    tws.forEach((tw) => tw.setTime(Number.MAX_SAFE_INTEGER));
                    this._tweens.delete(action.nodeTarget);
                }
            }
        });
    }

    private _pushTween(nodeTarget: Node, tw: Tween<any>) {
        const tws = this._tweens.get(nodeTarget);
        if (!tws) {
            this._tweens.set(nodeTarget, [tw]);
        } else {
            tws.push(tw);
        }
    }

    private _popTween(nodeTarget: Node, tw: Tween<any>) {
        const tws = this._tweens.get(nodeTarget);
        if (tws) {
            const i = tws.indexOf(tw);
            if (i !== -1) {
                tws.splice(i, 1);
            }
            if (tws.length === 0) {
                this._tweens.delete(nodeTarget);
            }
        }
    }

    private _executeAnimationActions(actions: AnimationAction[], currentTime: number) {
        actions.forEach(async (action) => {
            if (!action || !action.animation) {
                return;
            }
            if (action.orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (action.orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (action.orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }
            if (action.type === AnimationActionType.Play) {
                if (action.isAddAnimation) {
                    const [interruptEvent, interruptEventCancel] = Delay.event(EnhanceAnimationClip.EVENT_TYPE.ANIMATION_INTERRUPT, action.animation, undefined, true);
                    const waitResult = await Promise.race([action.animation.waitForFinished(), interruptEvent.then(() => 'interrupt')]);
                    interruptEventCancel();
                    if (waitResult === 'interrupt') {
                        return;
                    }
                } else if (action.isAppendAnimation) {
                    const [interruptEvent, interruptEventCancel] = Delay.event(EnhanceAnimationClip.EVENT_TYPE.ANIMATION_INTERRUPT, action.animation, undefined, true);
                    const waitResult = await Promise.race([action.animation.waitForLastFrame(), interruptEvent.then(() => 'interrupt')]);
                    interruptEventCancel();
                    if (waitResult === 'interrupt') {
                        return;
                    }
                }
                // 如果未勾選 isAddAnimation 就打斷所有之前等待的動畫，直接播放當前動畫
                if (!action.isAddAnimation) {
                    action.animation.emit(EnhanceAnimationClip.EVENT_TYPE.ANIMATION_INTERRUPT);
                }

                action.animation.play(action.animationClipName);
                const startTime = action.startTime / 1000 + currentTime;
                if (startTime) {
                    const state = action.animation.getState(action.animationClipName);
                    state?.setTime(startTime);
                    state?.sample();
                }
            } else if (action.type === AnimationActionType.Stop) {
                action.animation.stop();
            }
            if (action.updateSpeedRate) {
                const state = action.animation.getState(action.animationClipName);
                if (state) {
                    state.speed = action.speedRate;
                }
            }
        });
    }

    private _executeEnhanceAnimationActions(actions: EnhanceAnimationAction[], currentTime: number) {
        actions.forEach((action) => {
            if (!action || !action.animation) {
                return;
            }
            if (action.orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (action.orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (action.orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }
            const enhanceAnimation = action.animation.getComponent(EnhanceAnimation);
            if (!enhanceAnimation) {
                warn('EnhanceAnimationAction: No EnhanceAnimation component found.');
                return;
            }
            if (action.type === EnhanceAnimationActionType.Play) {
                enhanceAnimation.play(action.animationClipName, currentTime + action.startTime / 1000);
            } else if (action.type === EnhanceAnimationActionType.Stop) {
                enhanceAnimation.clips.find((clip) => clip.clipName === action.animationClipName)?.stop();
            }
        });
    }

    private _executeAudioActions(actions: AudioAction[], currentTime: number) {
        actions.forEach((action) => {
            if (!action) {
                return;
            }
            if (action.orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (action.orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (action.orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }
            if (action.type === AudioActionType.Play) {
                if (action.audioType === AudioType.Effect) {
                    this.game?.audio?.playEffect(action.audioName, action.loop, currentTime, action.volumeRate);
                } else if (action.audioType === AudioType.Music) {
                    const audioSource = this.game?.audio?.playMusic(action.audioName, action.musicIndex);
                    this.game?.audio?.setVolumeOf(audioSource, action.volumeRate);
                }
            } else if (action.type === AudioActionType.Stop) {
                if (action.audioType === AudioType.Effect) {
                    this.game?.audio?.stopEffect(action.audioName);
                } else if (action.audioType === AudioType.Music) {
                    const audioSource = this.game?.audio?.musicAudioSources[action.musicIndex];
                    this.game?.audio?.recycleAudioSource(audioSource);
                }
            }
        });
    }

    private _executeParticleActions(actions: ParticleAction[]) {
        actions.forEach(async (action) => {
            if (!action || !action.particle) {
                return;
            }
            if (action.orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (action.orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (action.orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }
            if (action.updateRandomSeed) {
                action.particle.randomSeed = action.randomSeedList[Math.floor(Math.random() * action.randomSeedList.length)];
            }
            if (action.changeLoopStatus) {
                action.particle.loop = action.isLoop;
            }
            if (action.type === ParticleActionType.Active) {
                action.particle.node.active = true;
                action.particle.stop();
                action.particle.clear();
                if (action.delayStart) {
                    await this._delay(0);
                }
                action.particle.play();
            } else if (action.type === ParticleActionType.Inactive) {
                action.particle.stop();
                action.particle.node.active = false;
            }
        });
    }

    private _executeParticle2DActions(actions: Particle2DAction[]) {
        actions.forEach(async (action) => {
            if (!action || !action.particle) {
                return;
            }
            if (action.orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (action.orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (action.orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }
            if (action.resetSystem) {
                action.particle.resetSystem();
                if (action.updateUVs) {
                    action.particle._simulator.updateUVs(true);
                }
            }
            if (action.enableFadeEmissionRate) {
                const startValue = action.fadeEmissionRateStartValue === -1 ? action.particle.emissionRate : action.fadeEmissionRateStartValue;
                const targetValue = action.fadeEmissionRateTargetValue;
                const duration = action.fadeEmissionRateDuration / 1000;
                action.particle.emissionRate = startValue;
                tween(action.particle)
                    .to(
                        duration,
                        { emissionRate: targetValue },
                        {
                            easing: (t) => action.fadeEmissionRateCurve.evaluate(t),
                        },
                    )
                    .start();
            }
        });
    }

    private _executeNeutrinoParticleActions(actions: NeutrinoParticleAction[]) {
        actions.forEach(async (action) => {
            if (!action.particle) {
                return;
            }
            if (action.orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (action.orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (action.orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }
            if (action.updatePauseState) {
                if (action.pauseState) {
                    action.particle.pauseSystem();
                } else {
                    action.particle.resumeSystem();
                }
            }
            if (action.updateEmitterPauseState) {
                if (action.emitterPauseState) {
                    action.particle.pauseGeneratorsSystem();
                } else {
                    action.particle.resumeGeneratorsSystem();
                }
            }
            if (action.type === NeutrinoParticleActionType.Restart) {
                action.particle.restart();
            }

            if (action.type === NeutrinoParticleActionType.ResetPosition) {
                if (action.resetPositionTargetNode) {
                    action.particle.node.worldPosition = action.resetPositionTargetNode.worldPosition;
                    action.particle.node.worldRotation = action.resetPositionTargetNode.worldRotation;
                }
                action.particle.resetPositionRotation();
            }

            if (action.type === NeutrinoParticleActionType.PauseEmitterOnEmpty) {
                if (action.particle.getNumParticles() === 0) {
                    action.particle.pauseGeneratorsSystem();
                    return;
                }
                Delay.event(NeutrinoComponentBase.EVENT_TYPE.ParticlesAllDisappear, action.particle).then(() => {
                    action.particle.pauseGeneratorsSystem();
                });
            }
        });
    }

    private _executeSkeletonActions(actions: SpineAnimationAction[], currentTime: number) {
        actions.forEach((action) => {
            if (!action || !action.skeleton) {
                return;
            }
            if (action.orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (action.orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (action.orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }
            if (action.changeSkeleton && action.skeletonData && action.skeletonData !== action.skeleton.skeletonData) {
                action.skeleton.skeletonData = action.skeletonData;
            }
            if (action.changeSkin) {
                action.skeleton.setSkin(action.skinName);
            }
            if (action.interruptAddAnimation) {
                action.skeleton.clearAnimation();
            }
            if (action.setToSetupPose) {
                action.skeleton.setToSetupPose();
            }
            if (action.setPaused) {
                action.skeleton.paused = action.paused;
            }

            if (action.actionType === SpineAnimationActionType.SetOrAddAnimation) {
                if (action.isAddAnimation) {
                    action.skeleton.addAnimation(action.animationTrackIndex, action.animationName, action.isLoop);
                } else {
                    action.skeleton.setAnimation(action.animationTrackIndex, action.animationName, action.isLoop);
                    if (action.startTime > 0) {
                        action.skeleton.defaultTrackTime = action.startTime / 1000;
                    }
                }
            } else if (action.actionType === SpineAnimationActionType.AppendAnimation) {
                action.skeleton.appendAnimation(action.animationName, action.isLoop, action.speedUpRate);
            } else if (action.actionType === SpineAnimationActionType.SetTimeScale) {
                action.skeleton.timeScale = action.timeScale;
            }

            if (currentTime) {
                // 只在有設定當前動畫時, 依據時長餘數取當前時間
                if (action.skeleton.loop && action.skeleton.animation) {
                    action.skeleton.defaultTrackTime = currentTime % action.skeleton.getDuration(action.skeleton.animation);
                } else {
                    action.skeleton.defaultTrackTime = currentTime;
                }
            }
        });
    }

    private _executeVapVideoActions(actions: VapVideoAction[]) {
        actions.forEach((action) => {
            if (!action || !action.vapVideoPlayContext) {
                return;
            }
            if (action.orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (action.orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (action.orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }

            const { vapName, isShared, isLoop, startTime, setPaused, paused } = action;

            if (setPaused && paused) {
                action.vapVideoPlayContext.pauseVap();
            } else {
                action.vapVideoPlayContext.playVap(vapName, isShared, isLoop, startTime / 1000).catch((e) => error('EnhanceAnimationClip: playVap failed.', e));
            }
        });
    }

    private _executeDragonBoneActions(actions: DragonBoneAnimationAction[], currentTime: number) {
        actions.forEach((action) => {
            if (!action || !action.armatureDisplay) {
                return;
            }
            if (action.orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (action.orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (action.orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }
            if (action.changeArmature) {
                action.armatureDisplay.armatureName = action.armatureName;
            }
            if (action.setTimeScale) {
                action.armatureDisplay.animation.timeScale = action.timeScale;
            }

            if (action.actionType === DragonBoneAnimationActionType.PlayAnimation) {
                action.armatureDisplay.playAnimation(action.animationName, action.playTimes);
                if (currentTime > 0) {
                    action.armatureDisplay.animation.gotoAndPlayByTime(action.animationName, currentTime, action.playTimes);
                } else if (action.startTime > 0) {
                    action.armatureDisplay.animation.gotoAndPlayByTime(action.animationName, action.startTime / 1000, action.playTimes);
                } else {
                    action.armatureDisplay.animation.play(action.animationName, action.playTimes);
                }
            } else if (action.actionType === DragonBoneAnimationActionType.FadeInAnimation) {
                action.armatureDisplay.animation.fadeIn(action.animationName, action.fadeInTime / 1000, action.playTimes);
            } else if (action.actionType === DragonBoneAnimationActionType.StopAnimation) {
                action.armatureDisplay.animation.stop();
            } else if (action.actionType === DragonBoneAnimationActionType.ResetAnimation) {
                action.armatureDisplay.animation.reset();
            }
        });
    }

    private _executeProcessorActions(actions: ProcessorAction[]) {
        actions.forEach(({ processor, type, orientationLimit }) => {
            if (!processor) {
                return;
            }
            if (orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }
            switch (type) {
                case ProcessorActionType.Play:
                    processor.play();
                    break;
                case ProcessorActionType.Pause:
                    processor.pause();
                    break;
                case ProcessorActionType.Resume:
                    processor.resume();
                    break;
                case ProcessorActionType.Stop:
                    processor.stop();
                    break;
                case ProcessorActionType.ForceStop:
                    processor.stop(false);
                    break;
                default:
                    break;
            }
        });
    }

    private _executeMovingEffectActions(actions: MovingEffectAction[]) {
        actions.forEach((action) => {
            if (!action || !action.target) {
                return;
            }
            if (action.orientationLimit !== OrientationLimitType.NoLimit) {
                if (
                    (action.orientationLimit === OrientationLimitType.LimitLandscape && !this.game.orientation.isLandscape) ||
                    (action.orientationLimit === OrientationLimitType.LimitPortrait && this.game.orientation.isLandscape)
                ) {
                    return;
                }
            }
            if (action.actionType === MovingEffectActionType.Play) {
                if (action.type === MovingEffectType.Bezier) {
                    const target = action.target as BezierMovingEffect;
                    const option: BezierMovingEffectOption = {
                        pathName: action.pathName,
                        speedRate: action.speedRate,
                        reverse: action.reverse,
                        overrideDuration: action.duration === 0 ? undefined : action.duration,
                        offset: action.positionOffset,
                    };
                    if (action.rescalePath) {
                        option.fromPos = action.from ? action.from.worldPosition : convertCenterPointToWorldSpace(action.startPosition);
                        option.toPos = action.to ? action.to.worldPosition : convertCenterPointToWorldSpace(action.endPosition);
                    }
                    if (action.overrideFixedRotate) {
                        option.fixedRotate = action.fixedRotate;
                    }
                    if (action.overrideSpeedCurve) {
                        option.speedCurve = action.overrideSpeedCurveUseEasing ? action.easingSpeedCurve : action.speedCurve;
                    }
                    target.play(option);
                } else if (action.type === MovingEffectType.Tween) {
                    const target = action.target as TweenMovingEffect;
                    if (action.overrideTweenCurve) {
                        target.xAxisCurve = action.xAxisCurve;
                        target.yAxisCurve = action.yAxisCurve;
                    }
                    if (action.overrideOriginScale) {
                        target.node.scale = action.originScale;
                    }
                    target.play({
                        startAt: action.startAt,
                        fromPos: action.from ? action.from.worldPosition : convertCenterPointToWorldSpace(action.startPosition),
                        toPos: action.to ? action.to.worldPosition : convertCenterPointToWorldSpace(action.endPosition),
                        overrideDuration: action.duration === 0 ? undefined : action.duration,
                        targetScale: action.targetScale,
                        onlyUpdateX: action.onlyUpdateX,
                        onlyUpdateY: action.onlyUpdateY,
                    });
                }
            } else {
                action.target.stop();
            }
        });
    }

    private _delay(duration: number) {
        return this.isRealTime ? Delay.timeout(duration) : Delay.resolve(duration, this);
    }
}
